import { InitialVariantAttributeConfig } from '@vend/business-components'
import { AddProductFastProps } from '@vend/business-components/dist/components/AddProductFast/components/AddProductFast'
import { ApiProduct } from '@vend/types'
import { useSyncExternalStore } from 'react'

interface VariantDefinition {
  id: string
  name: string
}

export interface NewVariantDetails {
  hasNewVariantAttribute: boolean
  newVariantAttribute: {
    attribute: VariantDefinition
    internalID: string
    values: string[]
  }
  selectedAttributeValues: Array<{
    value: string
    definition: VariantDefinition
  }>
}

export interface VariantPickerProps {
  close: (
    variantPromise: Promise<ApiProduct | NewVariantDetails | undefined>
  ) => void
  parentProduct: ApiProduct
  variantProducts: ApiProduct[]
  allowParentSelect: boolean
  showAddValue?: boolean
  initialVariantAttributeConfig?: InitialVariantAttributeConfig
}

interface AppProductState {
  variantPickerProps: VariantPickerProps | null
  addProductFastProps: AddProductFastProps | null
  showGiftCardModal: boolean
}

let appProductState: AppProductState = {
  variantPickerProps: null,
  addProductFastProps: null,
  showGiftCardModal: false,
}

let listeners: Array<() => void> = []

const addListener = (listener: () => void) => {
  listeners = [...listeners, listener]
}

const removeListener = (listener: () => void) => {
  listeners = listeners.filter(l => l !== listener)
}

const updateListeners = () => {
  listeners.forEach(listener => {
    listener()
  })
}

const subscribe = (cb: () => void) => {
  addListener(cb)
  return () => removeListener(cb)
}

const getState = () => {
  return appProductState
}

export const useAddProductToSaleStore = () => {
  const state = useSyncExternalStore<AppProductState>(subscribe, getState)

  const setAddProduct = (addProductFastProps: AddProductFastProps | null) => {
    appProductState = { ...getState(), addProductFastProps }
    updateListeners()
  }

  const setSelectVariant = (variantPickerProps: VariantPickerProps | null) => {
    appProductState = { ...getState(), variantPickerProps }
    updateListeners()
  }

  const addNewVariantToSelectProps = (result: NewVariantDetails) => {
    const { variantPickerProps } = getState()
    if (!variantPickerProps) {
      return
    }
    setSelectVariant({
      ...variantPickerProps,
      initialVariantAttributeConfig: {
        newVariantAttribute: result.newVariantAttribute,
        selectedAttributeValues: result.selectedAttributeValues,
      },
    })
  }

  const openGiftCardModal = () => {
    appProductState = { ...getState(), showGiftCardModal: true }
    updateListeners()
  }

  const closeGiftCardModal = () => {
    appProductState = { ...getState(), showGiftCardModal: false }
    updateListeners()
  }

  return {
    state,
    setAddProduct,
    setSelectVariant,
    addNewVariantToSelectProps,
    openGiftCardModal,
    closeGiftCardModal,
  }
}
